import {getOnixUrl, HttpService} from "../../../ui-kit/src/commons/http";

export type Member = {
  id: string,
  email: string,
  first_name: string,
  last_name: string,
  is_active: boolean,
  last_login: string | null,
  roles: string[],
}

export type MembersPagination = {
  total_pages: number,
  current_page: number,
  total_count: number,
  per_page: number,
}

export type RoleCount = {
  name: string,
  users_count: number,
}

export type Invite = {
  id: string,
  email: string,
  name: string,
  role: string,
  created_at: string | null,
  invited_by: string | null,
}

export type InvitesListResponse = {
  items: Invite[],
  pagination: MembersPagination,
}

export type InviteResponse = {
  item: Invite,
}

export type PublicInvite = {
  type: 'brand' | 'retailer',
  email: string,
  name: string,
  role: string,
  entity: {
    id: string,
    name: string,
    logo: string | null,
  },
}

export type PublicInviteResponse = {
  item: PublicInvite,
}

export type AcceptInviteResponse = {
  item: {
    id: string,
    email: string,
    first_name: string,
    last_name: string,
  },
}

type ErrorResponse = {
  status: 'error',
  substatus: string,
  message: string,
}

type SuccessResponse<T> = { status: 'success', substatus: string } & T;

type ApiResponse<T> = SuccessResponse<T> | ErrorResponse;

export class MembersApiError extends Error {
  substatus: string;

  constructor(substatus: string, message: string) {
    super(message);
    this.substatus = substatus;
  }
}

// Messages for the error substatuses that the members and invites endpoints return
const ERROR_MESSAGES: Record<string, string> = {
  user_not_found: 'No Vuuh account uses this email yet. Send them an invite instead.',
  email_taken: 'This email already has a Vuuh account or a pending invite.',
  password_not_strong_enough: 'Use at least 8 characters, including a number and a special character.',
  already_member: 'This person is already a member. Change the role in the members list.',
  last_admin: 'The company must keep at least one admin.',
  invalid_role: 'This role is not available.',
  forbidden: 'You do not have permission to manage members.',
  unauthorized: 'Your session has expired. Sign in again.',
  not_found: 'This member does not exist anymore. Refresh the list.',
};

function unwrap<T>(response: ApiResponse<T>): SuccessResponse<T> {
  if (!response || response.status !== 'success') {
    const error = response as ErrorResponse | undefined;
    const substatus = error && error.substatus ? error.substatus : 'unknown';
    const fallback = error && error.message ? error.message : 'An error occurred. Try again.';
    const message = ERROR_MESSAGES[substatus] ? ERROR_MESSAGES[substatus] : fallback;
    throw new MembersApiError(substatus, message);
  }
  return response;
}

export function listMembers(params: { page: number, perPage: number, search: string }) {
  const query: Record<string, string | number> = {page: params.page, per_page: params.perPage};
  if (params.search) {
    query.search = params.search;
  }
  return HttpService.GET<ApiResponse<{ items: Member[], pagination: MembersPagination }>>('/api/v1/members/', query)
    .then(unwrap);
}

export function listRoles() {
  return HttpService.GET<ApiResponse<{ items: RoleCount[] }>>('/api/v1/roles/')
    .then(unwrap)
    .then(response => response.items);
}

export function addMember(email: string, role: string) {
  return HttpService.POST<{ email: string, role: string }, ApiResponse<{ item: Member }>>(
    '/api/v1/members/', {email, role}
  ).then(unwrap).then(response => response.item);
}

export function setMemberRole(memberId: string, role: string) {
  return HttpService.PATCH<{ role: string }, ApiResponse<{ item: Member }>>(
    `/api/v1/members/${encodeURIComponent(memberId)}/`, {role}
  ).then(unwrap).then(response => response.item);
}

export function removeMember(memberId: string) {
  return HttpService.DELETE<ApiResponse<{}>>(`/api/v1/members/${encodeURIComponent(memberId)}/`)
    .then(unwrap);
}

// Invites. Only the admins of the current company can use these endpoints.

export function fetchInvites(params: { page: number, perPage: number }) {
  return HttpService.GET<ApiResponse<InvitesListResponse>>(
    '/api/v1/invites/', {page: params.page, per_page: params.perPage}
  ).then(unwrap);
}

export function createInvite(email: string, name: string, role: string) {
  return HttpService.POST<{ email: string, name: string, role: string }, ApiResponse<InviteResponse>>(
    '/api/v1/invites/', {email, name, role}
  ).then(unwrap);
}

export function cancelInvite(inviteId: string) {
  return HttpService.DELETE<ApiResponse<{}>>(`/api/v1/invites/${encodeURIComponent(inviteId)}/`)
    .then(unwrap);
}

// Public invite link. The person is not signed in, so the request has no Authorization header.

function publicRequest<R>(method: 'GET' | 'POST', path: string, data?: unknown): Promise<R> {
  const url = getOnixUrl(path);
  if (!url) {
    return Promise.reject(new MembersApiError('network', 'Vuuh is not available. Try again later.'));
  }
  const headers: Record<string, string> = {'Accept': 'application/json'};
  let body: string | undefined;
  if (data !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(data);
  }
  return fetch(url, {method, headers, body})
    .then(response => response.json() as Promise<R>)
    .catch(() => {
      throw new MembersApiError('network', 'We cannot connect to Vuuh. Check your connection and try again.');
    });
}

function inviteKeyUrl(key: string) {
  return `/api/v1/invites/by-key/${encodeURIComponent(key)}/`;
}

export function fetchPublicInvite(key: string) {
  return publicRequest<ApiResponse<PublicInviteResponse>>('GET', inviteKeyUrl(key))
    .then(unwrap);
}

export function acceptInvite(key: string, password: string) {
  return publicRequest<ApiResponse<AcceptInviteResponse>>('POST', inviteKeyUrl(key), {password})
    .then(unwrap);
}

export function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'An error occurred. Try again.';
}

export function getErrorSubstatus(err: unknown): string | null {
  if (err instanceof MembersApiError) {
    return err.substatus;
  }
  return null;
}
